import { dashboardUrl, powerBIdashboardUrl } from '../../service/config';
import { useState, useEffect, useMemo, useRef } from 'react';
import { Select, MenuItem, FormControl, SelectChangeEvent, TextField } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import '../DataPipeline/dataPipeline.scss';
import '../DataEstate/data-estate.scss';
import { getCustomDashboard } from '../../service/service';
import CustomDashboardIcon from '../../assets/Custom_Dashboard.svg';
import useStyles from '../landingScreen/headerStyles';
import Loader from '../Loader/loader';
import AnalyticsVisualization from 'components/framework/analyticsVisualization';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme } from "@mui/material/styles";

interface Dashboard {
	Dashboard_Url: string;
	Custom_Dashboard_Id: string;
	Dashboard_Name: string;
	Market: string;
	Actions:string;
}

const CustomDashboard = () => {
	const theme = useTheme();
	const classes = useStyles();
	const navigate = useNavigate();
	const { programName } = useParams<{ programName: string }>();
	const { marketName } = useParams<{ marketName: string }>();

	const [loading, setLoading] = useState(true);
	const [dashboardList, setDashboardList] = useState<Dashboard[]>([]);
	const [filteredDashboardList, setFilteredDashboardList] = useState<Dashboard[]>([]);
	const [searchText, setSearchText] = useState('');
	const [selectedDashboard, setSelectedDashboard] = useState<Dashboard | null>(null);
	const [dashboardUrlState, setDashboardUrlState] = useState<string>('');
	const [open, setOpen] = useState(false);
	const searchInputRef = useRef<HTMLInputElement | null>(null);
	const [hasAccess, setHasAccess] = useState<boolean>(true);

	const handleProgramClick = () => {
		const normalizedProgramName = (programName || '').toLowerCase().trim();
		const programMappings: { [key: string]: string } = {
			mdip: 'mdip-landing page',
			ibp: 'home page',
			mosaic: 'mosaic-landing page',
			edf: 'edf-landing page'
		};

		const dashboardKey = programMappings[normalizedProgramName] || '';
		const matchedUrl = dashboardKey ? dashboardUrlMappings[dashboardKey] : '';

		if (matchedUrl) {
			setDashboardUrlState(matchedUrl);
		}
	};

	const getInitialDashboard = (dashboards: Dashboard[]): Dashboard | null => {
		const ibpPageDashboard = dashboards.find((d) => d.Dashboard_Name.toLowerCase() === 'ibp - home page');
		const mdipPageDashboard = dashboards.find((d) => d.Dashboard_Name.toLowerCase() === 'mdip-landing page');
		const mosaicPageDashboard = dashboards.find((d) => d.Dashboard_Name.toLowerCase() === 'mosaic-landing page');
		const edfPageDashboard = dashboards.find((d) => d.Dashboard_Name.toLowerCase() === 'edf-landing page');

		if (programName?.toLowerCase().includes('ibp') && ibpPageDashboard) {
			return ibpPageDashboard;
		}
		if (programName?.toLowerCase().includes('mdip') && mdipPageDashboard) {
			return mdipPageDashboard;
		}
		if (programName?.toLowerCase().includes('mosaic') && mosaicPageDashboard) {
			return mosaicPageDashboard;
		}
		if (programName?.toLowerCase().includes('edf') && edfPageDashboard) {
			return edfPageDashboard;
		}
		return dashboards[0];
	};

	useEffect(() => {
		const activeId = document.getElementById('root');
		console.log(window.location.pathname);
		if (window.location.pathname.includes('/data-pipeline/programOps')) {
			if (activeId) {
				document.head.classList.add('theme-bg-blue');
				document.body.classList.add('theme-bg-blue');
			}
		}
	}, []);

	useEffect(() => {
		const fetchDashboardData = async () => {
			setLoading(true);
			setHasAccess(true);
			try {
				const tags = programName ? encodeURIComponent(programName) : '';
				const result = await getCustomDashboard({ tags });
				if(result?.status === 403) {
					setHasAccess(false);
					return;
				}
				let dashboards: Dashboard[] = result?.data?.list || [];

				if (marketName) {
					dashboards = dashboards.filter((dashboard) =>
						dashboard.Market.toLowerCase().includes(marketName.toLowerCase())
					);
				}

				setDashboardList(dashboards);
				setFilteredDashboardList(dashboards);
				
				if (dashboards.length > 0) {
					const initialDashboard = getInitialDashboard(dashboards);
					if (initialDashboard?.Custom_Dashboard_Id !== selectedDashboard?.Custom_Dashboard_Id) {
						setSelectedDashboard(initialDashboard);
					}
				}
			} catch (error) {
				console.error('Error fetching dashboard data:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchDashboardData();
	}, [programName, marketName]);

	useEffect(() => {
		if (!selectedDashboard) return;

		const dashboardKey = selectedDashboard.Dashboard_Name.toLowerCase();
		const matchedKey = Object.keys(dashboardUrlMappings).find((key) => dashboardKey.includes(key));

		let newUrl:string
		if(programName?.toLowerCase()==='edf'){
			newUrl=selectedDashboard.Actions
		}else{
			newUrl= matchedKey ? dashboardUrlMappings[matchedKey] : dashboardUrl.MDIPLandingPage;
		}

		if (newUrl !== dashboardUrlState) {
			setDashboardUrlState(newUrl);
		}
	}, [selectedDashboard]);

	useEffect(() => {
		const filtered = dashboardList.filter((dashboard) =>
			dashboard.Dashboard_Name.toLowerCase().includes(searchText.toLowerCase())
		);
		setFilteredDashboardList(filtered);
	}, [searchText, dashboardList]);

	const handleDashboardChange = (event: SelectChangeEvent<string>) => {
		const selectedDashboardId = event.target.value;

		const selectedDashboardUrl =
			dashboardList.find((d) => d.Custom_Dashboard_Id === selectedDashboardId)?.Dashboard_Url || '';

		if (selectedDashboardUrl) {
			setDashboardUrlState(selectedDashboardUrl);
		}
	};

	const shouldRedirectToPrograms = marketName === 'IBP Global - ReactJS POC';

    useEffect(() => {
        if (shouldRedirectToPrograms) {
            navigate('/data-pipeline/programOps/IBP/markets/ibp-dashboard', { replace: true });
        }
    }, [shouldRedirectToPrograms, navigate]);

    if (shouldRedirectToPrograms) {
        return <Loader />;
    }

	const dashboardUrlMappings: { [key: string]: string } = {
		'thailand mvs & gtm landing': dashboardUrl.MDIPLandingPage,
		'thailand mvs & gtm gold': dashboardUrl.MDIPGoldPage,
		'thailand mvs & gtm detailed': dashboardUrl.MDIPDetailedPage,
		timeseries: dashboardUrl.IBPGDOTimeseries,
		turkey: dashboardUrl.IBPTurkeyGDOTimeseries,
		vietnam: dashboardUrl.MDIPVIETNAM,
		pmf: dashboardUrl.MDIPPMF,
		'activity details': dashboardUrl.IBPGDOActivityDetail,
		'error board': dashboardUrl.IBPGDOErrorBoard,
		'is&oe board': dashboardUrl.IBPGDOIsoe,
		'main page': dashboardUrl.IBPGDOMainPage,
		'gdo status': dashboardUrl.IBPGDOStatus,
		'home page': dashboardUrl.IBPGDOHomePage,
		servicenow: dashboardUrl.IBPServiceNow,
		'gdo metric': dashboardUrl.IBPMetric,
		'taiwan gtm detailed': dashboardUrl.MDIPTAIWANGTMDETAILED,
		mt2dc: dashboardUrl.MDIPMT2DC,
		'panorama dashboard': dashboardUrl.MOSAIC,
		'synapse analytics dashboard': dashboardUrl.MOSAIC,
		'premosaic synapse analytics dashboard': dashboardUrl.MOSAIC,
		'e2e reconciliation': dashboardUrl.MOSAIC,
		'data quality monitoring dashboard': dashboardUrl.MOSAIC,
		'manual file monitoring report global': dashboardUrl.MOSAIC,
		'mdip-landing page': dashboardUrl.MDIPGeneralLanding,
		'mosaic-landing page': dashboardUrl.MosaicGeneralLanding,
		'mosaic icedq': powerBIdashboardUrl.ICEDQDashboard,
		'edf-landing page': dashboardUrl.EDFGeneralLanding,
	};

	const isPowerBI = dashboardUrlState.includes('powerbi.com');
	const dashboardClass = isPowerBI ? 'powerBiIframeContainer' : 'datapipelineCustomDashboardContainer';

	const analyticsVisualizationComponent = useMemo(() => {
		console.log('Rendering AnalyticsVisualization with URL:', dashboardUrlState);
		const otherParams = isPowerBI
		? { name: 'PowerBI', seamless: true }
		: { seamless: true };
		return (
			<AnalyticsVisualization
				params={{
					dashboardUrl: dashboardUrlState,
					className: dashboardClass,
					otherParams: otherParams,
				}}
			/>
		);
	}, [dashboardUrlState]);

	return (
		hasAccess && <div className="dashboard-panel-dataestate job-schedules-wrap">
			<div className="breadcrumb-custom" style={{ position: 'absolute', marginTop: '-33px' }}>
				<span className="job-menu" onClick={(e) => e.stopPropagation()} style={{ cursor: 'pointer' }}>
					<img
						src={CustomDashboardIcon}
						className={classes.jobIcon}
						style={{ marginTop: '-5px' }}
						alt="Custom Dashboards Icon"
					/>
					<span className="titleLink" style={{ color: `${theme.palette.textLight.dark}` }} onClick={() => navigate('/data-pipeline/programOpsDashboard')}>
						Program Ops Dashboards
					</span>

					<ChevronRightIcon sx={{color: 'textLight.dark', position: 'absolute'}} />
					{marketName && (
						<>
							<span
								className="titleLink"
								onClick={() => navigate(`/data-pipeline/programOps/${programName}/markets`)}
								style={{ paddingLeft: '22px', color: `${theme.palette.textLight.dark}` }}
							>
								{programName}
							</span>
							<ChevronRightIcon sx={{color: 'textLight.dark', position: 'absolute'}} />
						</>
					)}
					<FormControl className="w-64">
						<Select
							className={classes.selectPadding}
							value={selectedDashboard?.Custom_Dashboard_Id || ''}
							onChange={handleDashboardChange}
							open={open}
							onOpen={() => setOpen(true)}
							onClose={(event) => {
								if (searchInputRef.current?.contains(event.target as Node)) {
									return;
								}
								setSearchText('');
								setOpen(false);
							}}
							displayEmpty
							IconComponent={() => null}
							MenuProps={{
								PaperProps: {
									style: { maxHeight: 300, overflowY: 'auto' },
									onMouseDown: (e: any) => e.stopPropagation()
								}
							}}
							renderValue={() => (
								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										width: '100%'
									}}
								>
									<span
										onMouseDown={(e) => e.stopPropagation()}
										onClick={(e) => {
											e.stopPropagation();
											handleProgramClick();
										}}
										style={{ cursor: 'pointer', flexGrow: 1, fontSize: '20px', fontWeight: 600, color: `${theme.palette.textLight.dark}` }}
									>
										{marketName ?? programName ?? 'Select Dashboard'}
									</span>

									<ExpandMoreIcon 
										sx={{color: 'textLight.dark', cursor: 'pointer', height: '25px'}} 
										onClick={(e) => {
											e.stopPropagation();
											setOpen((prev) => !prev);
									}}/>
								</div>
							)}
						>
							<MenuItem disableRipple>
								<TextField
									inputRef={searchInputRef}
									fullWidth
									autoFocus
									placeholder="Search"
									variant="outlined"
									value={searchText}
									onChange={(e) => {
										setSearchText(e.target.value);
										setOpen(true);
									}}
									onKeyDown={(e) => e.stopPropagation()}
									onMouseDown={(e) => e.stopPropagation()}
									onClick={(e) => e.stopPropagation()}
									InputProps={{
										startAdornment: <SearchIcon style={{ marginRight: 8 }} />
									}}
								/>
							</MenuItem>
							{filteredDashboardList.map((dashboard) => {
								const dashboardKey = dashboard.Dashboard_Name.toLowerCase();
								let dashboardLink :any

								if(programName?.toLowerCase()==='edf'){
									dashboardLink = dashboard.Actions;
								}else{
									const matchedKey = Object.keys(dashboardUrlMappings).find((key) => dashboardKey.includes(key));
									dashboardLink= matchedKey ? dashboardUrlMappings[matchedKey] : dashboardUrl.MDIPLandingPage;
								}

								return (
									<MenuItem
										key={dashboard.Custom_Dashboard_Id}
										selected={selectedDashboard?.Custom_Dashboard_Id === dashboard.Custom_Dashboard_Id}
										onClick={(event) => {
											event.preventDefault();
											setSelectedDashboard(dashboard);
											setDashboardUrlState(dashboardLink);
											setSearchText('');
											setOpen(false);
										}}
										component="div"
									>
										<a
											href={dashboardLink}
											target="_blank"
											rel="noopener noreferrer"
											style={{
												textDecoration: 'none',
												color: 'inherit',
												display: 'block',
												width: '100%',
												height: '100%',
												padding: '8px 16px'
											}}
											onClick={(event) => {
												if (!event.ctrlKey && !event.metaKey && event.button === 0) {
													event.preventDefault();
													setSelectedDashboard(dashboard);
													setDashboardUrlState(dashboardLink);
													setSearchText('');
													setOpen(false);
												}
											}}
										>
											{dashboard.Dashboard_Name}
										</a>
									</MenuItem>
								);
							})}
						</Select>
					</FormControl>
				</span>
			</div>

			{marketName !== 'IBP Global - ReactJS POC' && <div className="program-list-container">{loading ? <Loader /> : analyticsVisualizationComponent}</div>}
		</div>
	);
};

export default CustomDashboard;
